// Date parsing utility for custom date formats
// Supports common formats: DD-MM-YYYY, MM-DD-YYYY, YYYY-MM-DD, DD/MM/YYYY, etc.

/**
 * Parse a date string according to a specified format
 * @param {string} dateString - The date string to parse
 * @param {string} format - The format pattern (e.g., 'DD-MM-YYYY')
 * @returns {Date} - Parsed Date object
 */
export const parseDate = (dateString, format) => {
    if (!dateString || !format) return new Date(0)

    // If format is not specified, try native Date parsing
    if (!format) {
        const nativeDate = new Date(dateString)
        return isNaN(nativeDate.getTime()) ? new Date(0) : nativeDate
    }

    // Normalize format to uppercase
    const normalizedFormat = format.toUpperCase()

    // Extract separator from format
    const separator = normalizedFormat.includes('-') ? '-' :
        normalizedFormat.includes('/') ? '/' :
            normalizedFormat.includes('.') ? '.' : '-'

    // Split format and date string by separator
    const formatParts = normalizedFormat.split(separator)
    const dateParts = dateString.split(separator)

    if (formatParts.length !== 3 || dateParts.length !== 3) {
        // Fallback to native parsing
        const nativeDate = new Date(dateString)
        return isNaN(nativeDate.getTime()) ? new Date(0) : nativeDate
    }

    // Map format parts to date values
    let day, month, year

    formatParts.forEach((part, index) => {
        const value = parseInt(dateParts[index], 10)

        switch (part) {
            case 'DD':
                day = value
                break
            case 'MM':
                month = value
                break
            case 'YYYY':
                year = value
                break
            case 'YY':
                // Convert 2-digit year to 4-digit
                year = value < 50 ? 2000 + value : 1900 + value
                break
            default:
                break
        }
    })

    // Validate parsed values
    if (isNaN(day) || isNaN(month) || isNaN(year)) {
        return new Date(0)
    }

    // Create Date object (month is 0-indexed in JavaScript)
    const date = new Date(year, month - 1, day)

    // Validate the date
    if (isNaN(date.getTime())) {
        return new Date(0)
    }

    return date
}

/**
 * Get common date format examples
 * @returns {Array} - Array of common date format examples
 */
export const getCommonDateFormats = () => [
    { format: 'DD-MM-YYYY', example: '25-12-2024' },
    { format: 'MM-DD-YYYY', example: '12-25-2024' },
    { format: 'YYYY-MM-DD', example: '2024-12-25' },
    { format: 'DD/MM/YYYY', example: '25/12/2024' },
    { format: 'MM/DD/YYYY', example: '12/25/2024' },
    { format: 'YYYY/MM/DD', example: '2024/12/25' },
    { format: 'DD.MM.YYYY', example: '25.12.2024' },
    { format: 'MM.DD.YYYY', example: '12.25.2024' },
    { format: 'YYYY.MM.DD', example: '2024.12.25' },
]

/**
 * Validate if a date string matches a given format
 * @param {string} dateString - The date string to validate
 * @param {string} format - The format pattern
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidDateFormat = (dateString, format) => {
    if (!dateString || !format) return false

    const parsedDate = parseDate(dateString, format)
    return parsedDate.getTime() !== new Date(0).getTime()
}
